import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	AccountingEventType,
	BillingCycleUnit,
	CellTypes,
	Consumer,
	ContractType,
	convertCurrencyToSign,
	Currency,
	getTotalValue,
	ItemDto,
	ItemPriceDto,
	PricingType,
	tableCalculation,
	UsageAggregationFunction,
	UsageAggregationScope,
	UsageAggregator,
} from '@received/pricing-model';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { DragAndDropSelectItem, Icon, Select } from '../_uiComponents';
import { EmptyPricingModel } from './EmptyPricingModel/EmptyPricingModel';
import { Contract, PricingModel, PricingModelTab } from '../../types/contractTypes';
import { PricingModelTabContentOptions } from '../PricingModelTabContent/PricingModelTabContentType';
import {
	BLANK_PRICING_MODEL_ID,
	BLANK_REFERRAL_PRICING_MODEL_ID,
	FLAT_RATE_TEMPLATE_NAME,
	TemplateCategory,
} from '../../constants/templateConstants';
import { CatalogModalV2 } from '..';
import { getBillingAccountId, getSupplierIdByCustomer, getSuppliers } from '../../utils/CustomerUtils';
import { PaymentTimeOptions } from '../../types/generalTypes';
import { calculateSubTotal, initialCyclicDay } from '../PricingModelTabContent/PricingModelTabContent.utils';
import { Customer } from '../../types/customerTypes';
import { getPricingModel } from '../../utils/pricingModelUtils';
import { getValueWithCurrencySign } from '../../utils/NumberUtils';
import { httpService, PathsConfig } from '../../services';
import { setOpenSuccessErrorModal } from '../../storeSlices/errorSuccessSlice';
import { NewPricingModelContent } from '../PricingModelTabContent';
import { ContractContentV2 } from '../PricingModelTabContent/TabComponents/ContractContent/ContractContentV2';
import { getBillingStartDate } from '../PricingModelTabContent/PricingModelTabContentActions';
import { onUpdateUsageProduct } from '../PricingModelTable/PricingModelTableActions';
import { requiredColumnsEnum } from '../PricingModelTable/PricingModelTable.utils';
import { billingCycleShortLabelsByEnum, checkExistingOrCreateItem } from '../PricingModelTable/TableRow/TableRow.utils';
import { defaultNetTerms } from '../NetTermsSelect/NetTermsSelect.utils';
import { UsageProduct } from '../../types/usageTypes';
import styles from './ContractAndPricingModelContainerV2.module.scss';

interface ContractAndPricingModelContainerProps {
	contract?: Contract;
	selectedCustomer?: Customer;
	pricingModel?: PricingModel;
	isEditMode?: boolean;
	selectedRowItem?: DragAndDropSelectItem;
	className?: string;
	pricingModelClassName?: string;
	tabContentType: PricingModelTabContentOptions;
	defaultSelectedTab?: number;
	contractType?: ContractType;
	setPricingModel: (value: PricingModel, instantUpdate?: boolean) => void;
	setProductsSelectedTab?: React.Dispatch<React.SetStateAction<number>>;
}

// The item (product / service name) is lifted out of the grid and used as the section title.
const getTabItemColumn = (tab: PricingModelTab) => tab.pricingModelTable?.columns?.find((col) => col.type === CellTypes.ITEM_REFERENCE);

const getTabItemName = (tab: PricingModelTab): string => {
	const itemColumn = getTabItemColumn(tab);
	const value = itemColumn ? tab.pricingModelTable?.rows?.[0]?.cells?.[itemColumn.id]?.cellValue : undefined;
	return (value as string) || '';
};

// Editable quantity is the COUNT `usage` cell; FIXED templates' `quantity` is a hidden formula derived
// from it (writing there is a no-op). Blank templates keep the count on `quantity`. Match by id, not
// "first COUNT column" (tiered/package have other COUNT columns).
const getTabQuantityColumn = (tab: PricingModelTab) => {
	const columns = tab.pricingModelTable?.columns || [];
	return (
		columns.find((col) => col.id === 'usage' && col.type === CellTypes.COUNT) ||
		columns.find((col) => col.id === requiredColumnsEnum.quantity && col.type === CellTypes.COUNT)
	);
};
const getTabQuantityValue = (tab: PricingModelTab) => {
	const column = getTabQuantityColumn(tab);
	return column ? tab.pricingModelTable?.rows?.[0]?.cells?.[column.id]?.cellValue : undefined;
};

// The price column (id 'price', else the first NUMBER column) — mirrors seedTabWithItem's lookup.
const getTabPriceColumn = (tab: PricingModelTab) =>
	tab.pricingModelTable?.columns?.find((col) => col.id === requiredColumnsEnum.price) ||
	tab.pricingModelTable?.columns?.find((col) => col.type === CellTypes.NUMBER);
const getTabUnitPrice = (tab: PricingModelTab): number | undefined => {
	const column = getTabPriceColumn(tab);
	const value = column ? tab.pricingModelTable?.rows?.[0]?.cells?.[column.id]?.cellValue : undefined;
	return value === '' || value === undefined || value === null ? undefined : Number(value);
};
// The row "Total": sum every row's amount (tiered/volume models spread it across rows), then apply
// the tab-level discount & tax via getTotalValue so it reflects the total, not the pre-discount subtotal.
const getTabAmount = (tab: PricingModelTab): number | undefined => {
	const rows = tab.pricingModelTable?.rows;
	if (!rows?.length) return undefined;
	return getTotalValue(calculateSubTotal(rows), tab.discount, tab.tax);
};

// A lone usage-aggregator column is surfaced as a "Usage product" selector (Pay as you go).
const getTabSingleUsageColumn = (tab: PricingModelTab) => {
	const usageColumns = (tab.pricingModelTable?.columns || []).filter((col) => col.type === CellTypes.USAGE_AGGREGATOR_REFERENCE);
	return usageColumns.length === 1 ? usageColumns[0] : undefined;
};
const getTabConnectedUsageProductId = (tab: PricingModelTab) => {
	const column = getTabSingleUsageColumn(tab);
	return column ? tab.pricingModelTable?.rows?.[0]?.cells?.[column.id]?.refObject?.usageProductId : undefined;
};

// Currency-signed value with short billing-cycle suffix ("$10 / mo."); no suffix for one-time/custom.
const formatAmountWithCycle = (value: number | undefined, tab: PricingModelTab): string => {
	const text = getValueWithCurrencySign(value ?? 0, convertCurrencyToSign(tab.currency ?? Currency.USD));
	const cycleUnit = tab.billingCycle?.unit;
	const label = cycleUnit && cycleUnit !== BillingCycleUnit.ONE_TIME && cycleUnit !== BillingCycleUnit.CUSTOM ? billingCycleShortLabelsByEnum[cycleUnit] : '';
	return label ? `${text} / ${label}` : text;
};

export const ContractAndPricingModelContainerV2 = ({
	contract,
	pricingModel = { tabs: [], sourcePricingModelIds: [] },
	selectedCustomer,
	isEditMode,
	selectedRowItem,
	className,
	pricingModelClassName,
	tabContentType,
	defaultSelectedTab,
	contractType,
	setPricingModel,
	setProductsSelectedTab,
}: ContractAndPricingModelContainerProps) => {
	// Latest-only guard for the async template swap (charge-type / pay-as-you-go toggle): rapid changes can
	// overlap, so only the most recent call is allowed to commit — stale in-flight results are dropped.
	const templateSwapSeq = useRef(0);
	const [openModal, setOpenModal] = useState(false);
	const [selectedTab, setSelectedTab] = useState<number>(0);
	const [selectedCommonModel, setSelectedCommonModel] = useState<PricingModel>();
	const [supplierEntities, setSupplierEntities] = useState<Customer[]>([]);
	// Row index whose inline editor (ContractContentV2) is expanded; null = none.
	const [editingIndex, setEditingIndex] = useState<number | null>(null);
	// Current search text in an item selector, used to offer "create new item" when nothing matches.
	const [itemSearch, setItemSearch] = useState('');
	// Catalog items for the "Add new price" dropdown.
	const [itemsList, setItemsList] = useState<ItemDto[]>([]);
	const [itemPrices, setItemPrices] = useState<ItemPriceDto[]>([]);
	// Pricing-model templates for the per-section "pricing model" dropdown.
	const [templates, setTemplates] = useState<PricingModel[]>([]);
	// Usage products — used to tag matching catalog items with the metered icon in the item selector.
	const [usageProducts, setUsageProducts] = useState<UsageProduct[]>([]);
	// Keys currently showing a draft "new price" item picker (becomes a line item once an item is chosen).
	const [draftPrice, setDraftPrice] = useState<Record<string, boolean>>({});

	// Lower-cased usage-product names, so items that are usage products get the metered icon.
	const usageProductNames = useMemo(() => new Set(usageProducts.map((product) => (product.name || '').trim().toLowerCase())), [usageProducts]);

	// Item selector options grouped by category; usage products get the metered icon. Category headers
	// are non-selectable separators.
	const buildItemOptions = (items: ItemDto[]) => {
		const groups = new Map<string, ItemDto[]>();
		items.forEach((item) => {
			const category = item.category?.trim() || 'Other';
			if (!groups.has(category)) groups.set(category, []);
			groups.get(category)?.push(item);
		});
		const options: any[] = [];
		Array.from(groups.keys())
			.sort((a, b) => a.localeCompare(b))
			.forEach((category) => {
				options.push({
					label: category,
					value: `__category__${category}`,
					isDisabled: true,
					customComponent: () => <div className={styles.itemCategoryHeader}>{category}</div>,
				});
				groups.get(category)?.forEach((item) => {
					const isUsage = usageProductNames.has((item.name || '').trim().toLowerCase());
					options.push({
						label: item.name,
						value: item.id || item.name,
						customComponent: () => (
							<div className={styles.itemOption}>
								<Icon imgType={isUsage ? 'meteredHash' : 'pricing'} color='neutral400' width={1.4} />
								<span className={styles.itemOptionLabel}>{item.name}</span>
							</div>
						),
					});
				});
			});
		return options;
	};

	// Memoized so a fresh array each render doesn't reset the Select's selection (keyed on data ref).
	const itemOptions = useMemo(() => buildItemOptions(itemsList), [itemsList, usageProductNames]);

	// Icon for the selected item in a selector's input — metered for usage products, pricing otherwise.
	const itemInputIcon = (nameOrValue?: string) => {
		if (!nameOrValue) return undefined;
		const item = itemsList.find((it) => (it.id || it.name) === nameOrValue || it.name === nameOrValue);
		const name = (item?.name || nameOrValue).trim().toLowerCase();
		return { imgType: usageProductNames.has(name) ? ('meteredHash' as const) : ('pricing' as const), color: 'neutral400' as const, width: 1.4 };
	};
	// Pricing-model dropdown options split by charge type: In advance = Fixed, Pay as you go = Usage.
	const templateOptionsByChargeType = useMemo(() => {
		const toOption = (template: PricingModel) => ({
			label: template.name as string,
			value: template.id as string,
			customComponent: () => (
				<div className={styles.itemOption}>
					<Icon imgType='pricing' color='neutral400' width={1.4} />
					<span className={styles.itemOptionLabel}>{template.name}</span>
				</div>
			),
		});
		const byCategory = (...categoryNames: string[]) =>
			templates
				.filter((template) => template.id && template.name && categoryNames.includes((template.categoryName || '').toLowerCase()))
				.map(toOption);
		return {
			// Fixed = In advance; Usage = Pay as you go. Services models (Installation/Platform/Base fee) are excluded.
			fixed: byCategory(TemplateCategory.FIXED),
			usage: byCategory(TemplateCategory.USAGE),
		};
	}, [templates]);

	// Pay-as-you-go items list Usage models; everything else lists Fixed (the default).
	const getChargeTypeOptions = (isPayAsYouGo?: boolean) =>
		isPayAsYouGo ? templateOptionsByChargeType.usage : templateOptionsByChargeType.fixed;

	const openDraftPrice = (key: string) => setDraftPrice((prev) => ({ ...prev, [key]: true }));
	const closeDraftPrice = (key: string) => setDraftPrice((prev) => ({ ...prev, [key]: false }));

	const { t } = useTranslation('translation');
	const dispatch = useDispatch();
	const navigate = useNavigate();

	useEffect(() => {
		setSelectedTab(defaultSelectedTab || 0);
	}, [defaultSelectedTab]);

	useEffect(() => {
		getSupplierEntities();
	}, [selectedCustomer]);

	useEffect(() => {
		getItemsData();
	}, []);

	const getItemsData = async () => {
		try {
			const [itemsRes, pricesRes, categoriesRes, usageRes] = await Promise.all([
				httpService({ dispatch, showLoader: false, path: PathsConfig.getItems }),
				httpService({ dispatch, showLoader: false, path: PathsConfig.getItemPriceItems }),
				httpService({ dispatch, showLoader: false, path: PathsConfig.getCategoriesList, params: { includePricingModels: 'true' } }),
				httpService({ dispatch, showLoader: false, path: PathsConfig.getUsageProductList, params: { isDraft: false } }),
			]);
			setItemsList(itemsRes.data || []);
			setItemPrices(pricesRes.data || []);
			setUsageProducts(usageRes.data || []);
			// Flatten templates nested under categories, tagging each with its category name so the
			// dropdown can filter by charge type.
			setTemplates(
				(categoriesRes.data || []).flatMap((category: { name?: string; pricingModels?: PricingModel[] }) =>
					(category.pricingModels || []).map((model) => ({ ...model, categoryName: model.categoryName || category.name })),
				),
			);
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		}
	};

	const getSupplierEntities = async () => {
		const companies = await getSuppliers(dispatch);
		setSupplierEntities(companies);
	};

	const addTabs = (tabs: PricingModelTab[]): PricingModelTab[] => {
		const newTabs = [...tabs];

		newTabs.forEach((tab) => {
			// Product-sourced models can arrive without accountingEventType, leaving no Billing Schedule.
			// Default it so the schedule renders: USAGE → USAGE_INVOICE, else INVOICE.
			if (!tab.accountingEventType) {
				tab.accountingEventType = tab.pricingType === PricingType.USAGE ? AccountingEventType.USAGE_INVOICE : AccountingEventType.INVOICE;
			}
			// Billing period is inherited from the contract level so every tab shares one period;
			// falls back to today when the contract has no period yet.
			const billingStartDate = contract?.activationStartDate ? dayjs(contract.activationStartDate).toDate() : dayjs().toDate();
			const billingEndDate = contract?.activationEndDate
				? dayjs(contract.activationEndDate).toDate()
				: tab.billingCycle?.unit == BillingCycleUnit.ONE_TIME
				? dayjs().toDate()
				: null;
			tab.billingStartDate = billingStartDate;
			tab.billingEndDate = billingEndDate;
			tab.issueDay =
				tab?.billingCycle?.unit === BillingCycleUnit.QUARTERLY
					? initialCyclicDay
					: getBillingStartDate(tab?.billingCycle, billingStartDate, billingEndDate);
			// Currency is contract-level; inherit from existing tabs (incl. each row's item-price currency).
			// First tab has no sibling, so fall back to USD — undefined rendered "undefined120" in the Summary.
			const contractCurrency = pricingModel?.tabs?.[0]?.currency ?? Currency.USD;
			tab.currency = contractCurrency;
			if (tab.pricingModelTable?.rows) {
				tab.pricingModelTable.rows = tab.pricingModelTable.rows.map((row) =>
					row?.itemPrice ? { ...row, itemPrice: { ...row.itemPrice, currency: contractCurrency } } : row,
				);
			}
			tab.netTerms = selectedCustomer?.netTerms ? Number(selectedCustomer?.netTerms) : defaultNetTerms;
			tab.supplierId = getSupplierIdByCustomer(selectedCustomer, supplierEntities);
			// Preserve the template's payment time; only fall back to a default when the template doesn't specify one.
			tab.paymentTime =
				tab.paymentTime ??
				(tab.pricingType == PricingType.USAGE ? PaymentTimeOptions.AFTER_USE : PaymentTimeOptions.DURING_USE);
			if (tab?.billingDetails) {
				if (selectedCustomer?.billingIntegration) {
					tab.billingDetails = { ...selectedCustomer?.billingIntegration };
				} else {
					tab.billingDetails.billingAccountId = getBillingAccountId(supplierEntities);
				}
			}
			tab.pricingModelTableTotal = calculateSubTotal(tab.pricingModelTable.rows);
			tab.issueDate = dayjs().toDate();
			tab.tax = selectedCustomer?.tax ? +selectedCustomer?.tax : 0;
			if (selectedCustomer?.netTerms) {
				tab.netTerms = selectedCustomer.netTerms;
			}
			if (tab.revenueSettings) {
				tab.revenueSettings.postingDay = initialCyclicDay;
			}
			tab.cryptoSettings = selectedCustomer?.cryptoSettings;
			// Settings (Debit Entity/Payment Method/Bank Transfer/Note) are contract-level; inherit from tab[0].
			const contractTermsTab = pricingModel?.tabs?.[0];
			if (contractTermsTab) {
				tab.supplierId = contractTermsTab.supplierId;
				tab.note = contractTermsTab.note;
				tab.billingDetails = { ...tab.billingDetails, ...contractTermsTab.billingDetails };
				tab.cryptoSettings = contractTermsTab.cryptoSettings ? { ...contractTermsTab.cryptoSettings } : tab.cryptoSettings;
			}
			tab.templatePricingModelTabId = tab.id;
			delete tab?.cryptoSettings?.id;
			// delete id so api will generate a new object instead of updating an existing one
			delete tab.id;
		});

		return newTabs;
	};

	const handleAddModel = (model: PricingModel, fromTemplate?: boolean) => {
		const newModel = { ...pricingModel };
		const addedTabs = addTabs(model.tabs);
		// Keep sourcePricingModelIds index-aligned with tabs (see onTabClose): push the id once per tab the
		// model adds, or the arrays desync and onTabClose drops the wrong id.
		if (model.id) {
			addedTabs.forEach(() => newModel.sourcePricingModelIds?.push(model.id!));
		}
		newModel.tabs = [...newModel.tabs, ...addedTabs];
		newModel.tabs.forEach((tab, index) => {
			tab.index = index;
		});

		// when creating a pricing model from a template with no name yet, adopt the template's name
		const hasNoName = !newModel.name?.trim() || newModel.name === t('newPricingModelTitle');
		if (fromTemplate && model.name && hasNoName && tabContentType !== PricingModelTabContentOptions.CONTRACT) {
			newModel.name = model.name;
		}

		setSelectedTab(newModel.tabs.length - 1);
		setProductsSelectedTab?.(newModel.tabs.length - 1);
		setPricingModel(newModel, true);
	};

	const updatePricingModelTab = (pricingModelTab: PricingModelTab, instantUpdate?: boolean) => {
		const newModel = { ...pricingModel };
		newModel.tabs.splice(selectedTab, 1, pricingModelTab);
		setPricingModel(newModel, instantUpdate);
	};

	// Line-item cards each update their own tab by index (not the single `selectedTab`).
	const updatePricingModelTabByIndex = (index: number, pricingModelTab: PricingModelTab, instantUpdate?: boolean) => {
		const newModel = { ...pricingModel, tabs: [...pricingModel.tabs] };
		newModel.tabs.splice(index, 1, pricingModelTab);
		setPricingModel(newModel, instantUpdate);
	};

	// Row "Quantity" (In advance): write the quantity into every row's quantity cell and recalculate.
	const onChangeRowQuantity = ({
		index,
		value,
		flushToServer,
	}: {
		index: number;
		value: string;
		flushToServer: boolean;
	}) => {
		const tab = pricingModel.tabs[index];
		const table = tab?.pricingModelTable;
		const quantityColumn = getTabQuantityColumn(tab);
		if (!table || !quantityColumn) return;
		const quantity = value === '' ? '' : Number(value);
		const rows = (table.rows || []).map((row) => ({
			...row,
			cells: { ...row.cells, [quantityColumn.id]: { ...row.cells[quantityColumn.id], cellValue: quantity } },
		}));
		const { table: recalculated } = tableCalculation({ ...table, rows }, Consumer.RECEIVED_UI);
		updatePricingModelTabByIndex(index, { ...tab, pricingModelTable: recalculated }, flushToServer);
	};

	const onStartBlank = async () => {
		const emptyPricingModel = await getPricingModel(
			contractType === ContractType.REFERRAL ? BLANK_REFERRAL_PRICING_MODEL_ID : BLANK_PRICING_MODEL_ID,
			dispatch,
		);
		handleAddModel(emptyPricingModel);
	};

	// If the item name matches a usage product and the tab has one usage-aggregator column, create the
	// aggregator (SUM / Per Document) and write it into tab.pricingModelTable in place.
	const connectMatchingUsageProduct = async (tab: PricingModelTab, itemName?: string) => {
		const columns = tab.pricingModelTable?.columns || [];
		const usageColumns = columns.filter((col) => col.type === CellTypes.USAGE_AGGREGATOR_REFERENCE);
		if (usageColumns.length !== 1 || !itemName?.trim()) return;
		const match = usageProducts.find((product) => (product.name || '').trim().toLowerCase() === itemName.trim().toLowerCase());
		const connectedId = tab.pricingModelTable?.rows?.[0]?.cells?.[usageColumns[0].id]?.refObject?.usageProductId;
		if (!match?.id || match.id === connectedId) return;
		try {
			const res = await httpService({
				dispatch,
				path: PathsConfig.createUsageAggregator,
				data: {
					usageProductId: match.id,
					name: match.name,
					usageProduct: match,
					aggregationFunction: UsageAggregationFunction.SUM,
					aggregationScope: UsageAggregationScope.DOCUMENT,
				},
			});
			const usageAggregator: UsageAggregator = res.data;
			const columnIndex = columns.findIndex((col) => col.id === usageColumns[0].id);
			onUpdateUsageProduct(
				{ usageAggregator, column: usageColumns[0] as any, columnIndex },
				tab.pricingModelTable,
				(updatedTable) => {
					tab.pricingModelTable = updatedTable;
				},
				() => {},
			);
		} catch (error) {
			dispatch(setOpenSuccessErrorModal({ responseError: error }));
		}
	};

	// Seed a new tab's first row from a picked catalog item: name into the item cell, catalog price
	// (matched by item id) into the price cell + itemPrice.
	const seedTabWithItem = (tab: PricingModelTab, item: ItemDto) => {
		const table = tab.pricingModelTable;
		const itemColumn = getTabItemColumn(tab);
		if (!table?.rows?.length || !itemColumn) return;

		const priceColumn = table.columns?.find((col) => col.id === 'price') || table.columns?.find((col) => col.type === CellTypes.NUMBER);
		const matchingPrice = itemPrices.find((price) => price.itemId === item.id || price.item?.id === item.id);

		table.rows = table.rows.map((row, rowIndex) => {
			const cells = { ...row.cells, [itemColumn.id]: { ...row.cells[itemColumn.id], cellValue: item.name } };
			if (rowIndex === 0 && priceColumn && matchingPrice) {
				cells[priceColumn.id] = { ...row.cells[priceColumn.id], cellValue: matchingPrice.price };
			}
			return {
				...row,
				cells,
				...(rowIndex === 0 && matchingPrice ? { itemPrice: { ...matchingPrice, id: undefined, currency: tab.currency } } : {}),
			};
		});

		const { table: recalculated } = tableCalculation(table, Consumer.RECEIVED_UI);
		tab.pricingModelTable = recalculated;
		tab.pricingModelTableTotal = calculateSubTotal(recalculated.rows);

		// The tab is the line item, so the chosen item is also its product name — keep name/productName in sync.
		if (item.name) {
			tab.name = item.name;
			tab.productName = item.name;
		}
	};

	// Swap a section's pricing model: apply the template's price table to the tab while keeping
	// contract-level context (period, currency, supplier). preserveRowValues (charge-type toggle) merges
	// the user's edited cell values into the new table.
	const onSelectPricingTemplate = async (
		index: number,
		templateId: string,
		paymentTime?: PaymentTimeOptions,
		preserveRowValues = false,
		pricingTypeOverride?: PricingType,
	) => {
		const seq = ++templateSwapSeq.current;
		const template = await getPricingModel(templateId, dispatch);
		const templateTab = template?.tabs?.[0];
		if (!templateTab) return;

		const tab = pricingModel.tabs[index];
		// Preserve the selected item across the swap — don't reset to the template's default item name.
		const currentItemName = getTabItemName(tab);
		const contractCurrency = pricingModel?.tabs?.[0]?.currency ?? Currency.USD;
		const table = templateTab.pricingModelTable;
		if (table?.rows) {
			table.rows = table.rows.map((row: any) => (row?.itemPrice ? { ...row, itemPrice: { ...row.itemPrice, currency: contractCurrency } } : row));
		}

		// Charge-type toggle keeps the same model, so columns line up 1:1. Merge the user's edited NUMBER/COUNT
		// cells (by column id) into the new table so switching doesn't wipe the row; hidden formulas are
		// recomputed below and the metered `usage` column has no counterpart so it's skipped.
		if (preserveRowValues && table?.rows && tab.pricingModelTable?.rows) {
			const oldRows = tab.pricingModelTable.rows;
			const mergeableColumns = (table.columns || []).filter((col: any) => col.type === CellTypes.NUMBER || col.type === CellTypes.COUNT);
			table.rows = table.rows.map((row: any, rowIndex: number) => {
				const oldCells = oldRows[rowIndex]?.cells;
				if (!oldCells) return row;
				const cells = { ...row.cells };
				mergeableColumns.forEach((col: any) => {
					const oldValue = oldCells[col.id]?.cellValue;
					if (oldValue !== undefined && oldValue !== null && cells[col.id]) {
						cells[col.id] = { ...cells[col.id], cellValue: oldValue };
					}
				});
				return { ...row, cells };
			});
		}

		const { table: recalculated } = tableCalculation(table, Consumer.RECEIVED_UI);
		const newItemColumn = recalculated.columns?.find((col) => col.type === CellTypes.ITEM_REFERENCE);
		if (currentItemName && newItemColumn) {
			recalculated.rows = recalculated.rows.map((row) => ({
				...row,
				cells: { ...row.cells, [newItemColumn.id]: { ...row.cells[newItemColumn.id], cellValue: currentItemName } },
			}));
		}

		const newTab: PricingModelTab = {
			...tab,
			name: templateTab.name || template.name || tab.name,
			// Override wins so the pay-as-you-go toggle sets USAGE/DEFAULT by the chosen charge category —
			// a usage template whose first tab is DEFAULT must not flip the toggle back off.
			pricingType: pricingTypeOverride ?? templateTab.pricingType ?? tab.pricingType,
			// Adopt the template's billing cycle (Services = One-time, Platform Fee = yearly); else the tab keeps its old cycle.
			billingCycle: templateTab.billingCycle ?? tab.billingCycle,
			pricingCycle: templateTab.pricingCycle ?? tab.pricingCycle,
			pricingCycleActivation: templateTab.pricingCycleActivation ?? tab.pricingCycleActivation,
			paymentTime: paymentTime ?? tab.paymentTime,
			pricingModelTable: recalculated,
			pricingModelTableTotal: calculateSubTotal(recalculated.rows),
			templatePricingModelTabId: templateTab.id,
		};

		// Re-apply the item→usage match — the new template comes with its own default usage product.
		await connectMatchingUsageProduct(newTab, currentItemName);

		// Drop this result if a newer swap started while we awaited, so out-of-order commits can't clobber it.
		if (seq !== templateSwapSeq.current) return;

		const newTabs = pricingModel.tabs.map((existing, i) => (i === index ? newTab : existing));
		// Keep sourcePricingModelIds index-aligned so the dropdown reflects the used template.
		const newSourceIds = [...(pricingModel.sourcePricingModelIds || [])];
		newSourceIds[index] = templateId;
		setPricingModel({ ...pricingModel, tabs: newTabs, sourcePricingModelIds: newSourceIds }, true);
	};

	// Pay-as-you-go toggle: swap the item between a Fixed and a Usage pricing model, keeping the same model
	// by name in the target category and carrying row values over. Turning it on forces billing In arrears;
	// turning it off resets timing to In advance. Falls back to the category's "Flat rate", then to just
	// flipping the usage axis + timing directly.
	const onTogglePayAsYouGo = (index: number, isPayAsYouGo: boolean) => {
		// Wait for templates: the swap must land on a real usage/fixed table (both categories seed a "Flat
		// rate"), so once loaded a target is always found. Toggling before then would flip pricingType/
		// paymentTime while keeping the old table — an inconsistent USAGE tab with fixed columns.
		if (!templates.length) return;
		const category: TemplateCategory = isPayAsYouGo ? TemplateCategory.USAGE : TemplateCategory.FIXED;
		const paymentTime = isPayAsYouGo ? PaymentTimeOptions.AFTER_USE : PaymentTimeOptions.DURING_USE;
		const pricingType = isPayAsYouGo ? PricingType.USAGE : PricingType.DEFAULT;
		const inCategoryNamed = (name: string) => (template: PricingModel) =>
			(template.categoryName || '').toLowerCase() === category && (template.name || '').trim().toLowerCase() === name;
		// Deterministic pick: duplicate (category, name) pairs can exist and `templates` order isn't
		// guaranteed, so sort matches by id rather than taking the first.
		const findInCategory = (name: string) =>
			templates.filter(inCategoryNamed(name)).sort((a, b) => (a.id || '').localeCompare(b.id || ''))[0];
		const currentTemplateId = pricingModel.sourcePricingModelIds?.[index];
		const currentModelName = (templates.find((tpl) => tpl.id === currentTemplateId)?.name || '').trim().toLowerCase();
		const target = (currentModelName && findInCategory(currentModelName)) || findInCategory(FLAT_RATE_TEMPLATE_NAME);
		if (target?.id) {
			onSelectPricingTemplate(index, target.id, paymentTime, true, pricingType);
		}
	};

	// "Add new price" — appends a line item seeded from the Flat rate template (falls back to blank).
	// A picked item seeds its name and catalog price into the new line's first row.
	const onAddNewPrice = async (item?: ItemDto) => {
		const flatRateTemplate = templates.find(
			(template) =>
				(template.categoryName || '').toLowerCase() === TemplateCategory.FIXED &&
				(template.name || '').toLowerCase() === FLAT_RATE_TEMPLATE_NAME,
		);
		const defaultModelId =
			contractType === ContractType.REFERRAL ? BLANK_REFERRAL_PRICING_MODEL_ID : flatRateTemplate?.id || BLANK_PRICING_MODEL_ID;
		const defaultModel = await getPricingModel(defaultModelId, dispatch);
		const preparedTabs = addTabs(defaultModel.tabs);
		if (item) preparedTabs.forEach((tab) => seedTabWithItem(tab, item));

		const newTabs = [...pricingModel.tabs, ...preparedTabs];
		newTabs.forEach((tab, i) => {
			tab.index = i;
		});
		// Keep sourcePricingModelIds index-aligned with tabs (see onTabClose).
		const newSourceIds = [...(pricingModel.sourcePricingModelIds || [])];
		newSourceIds.push(defaultModelId);

		setSelectedTab(newTabs.length - 1);
		setProductsSelectedTab?.(newTabs.length - 1);
		// Open the new line item's editor so it's ready to configure.
		setEditingIndex(newTabs.length - 1);
		setPricingModel({ ...pricingModel, tabs: newTabs, sourcePricingModelIds: newSourceIds }, true);
	};

	// Offer a "Create <text>" action in an item selector when the typed text matches no catalog item.
	const createItemButton = (onCreated: (item: ItemDto) => void) =>
		itemSearch && !itemsList.some((item) => item.name.toLowerCase() === itemSearch.toLowerCase())
			? {
					title: t('createItemNamed', { name: itemSearch }),
					onPress: async () => {
						const created = await checkExistingOrCreateItem(itemsList, itemSearch, dispatch);
						setItemSearch('');
						if (created) {
							await getItemsData();
							onCreated(created);
						}
					},
			  }
			: undefined;

	const filterItemOptions = (value: string) => {
		setItemSearch(value);
		return buildItemOptions(itemsList.filter((item) => item.name.toLowerCase().includes(value.toLowerCase())));
	};

	const onTabClose = (index: number) => {
		if (selectedTab != 0) {
			setSelectedTab(selectedTab - 1);
			setProductsSelectedTab?.(selectedTab - 1);
		}

		let tempModel = { ...pricingModel };
		const newTabs = [...tempModel.tabs.map((tab) => ({ ...tab }))];
		newTabs.splice(index, 1);

		if (tempModel?.sourcePricingModelIds) {
			const newTemplateIds = [...tempModel.sourcePricingModelIds];
			newTemplateIds.splice(index, 1);
			tempModel = { ...tempModel, sourcePricingModelIds: newTemplateIds };
		}
		setPricingModel({ ...tempModel, tabs: newTabs }, true);
	};

	return (
		<div className={clsx(styles.container, !pricingModel?.tabs?.length && styles.pricingModel, className)} data-testid='pricing-model-container'>
			{tabContentType === PricingModelTabContentOptions.CONTRACT ? (
					<div className={styles.itemsTableWrapper}>
						<div className={styles.itemsTable}>
							<div className={styles.itemsHeaderRow}>
								<div className={clsx(styles.itemsHeaderCell, styles.cellItem)}>Item</div>
								<div className={clsx(styles.itemsHeaderCell, styles.cellQuantity)}>Quantity</div>
								<div className={clsx(styles.itemsHeaderCell, styles.cellPrice)}>Price</div>
								<div className={clsx(styles.itemsHeaderCell, styles.cellTotal)}>Total</div>
								<div className={clsx(styles.itemsHeaderCell, styles.cellActions)} />
							</div>
							{pricingModel.tabs.map((tab, index) => {
								const itemName = getTabItemName(tab);
								const itemIcon = itemInputIcon(itemName);
								const isPayAsYouGo = tab.pricingType === PricingType.USAGE;
								// Pay as you go is only offered when the item has a matching usage product (matched by name) —
								// that product drives the metered quantity. Keep it shown once on so it can still be turned off.
								const hasMatchingUsageProduct = usageProductNames.has((itemName || '').trim().toLowerCase());
								// When Pay as you go is on the quantity is that matching usage product, shown read-only (no picker).
								const connectedUsageProductId = getTabConnectedUsageProductId(tab);
								const payAsYouGoUsageProductName =
									usageProducts.find((product) => product.id === connectedUsageProductId)?.name ||
									usageProducts.find((product) => (product.name || '').trim().toLowerCase() === (itemName || '').trim().toLowerCase())?.name ||
									itemName;
								const singleUsageColumn = getTabSingleUsageColumn(tab);
								const quantityColumn = getTabQuantityColumn(tab);
								const isExpanded = editingIndex === index;
								// Inline editor: charge-type-filtered pricing options, and flat-rate detection to hide add/remove-row.
								const tabTemplateOptions = getChargeTypeOptions(isPayAsYouGo);
								const sourceTemplateId = pricingModel.sourcePricingModelIds?.[index];
								const templateDefaultValue =
									sourceTemplateId && tabTemplateOptions.some((option) => option.value === sourceTemplateId) ? sourceTemplateId : tab.name || '';
								const selectedTemplate = templates.find((tpl) => tpl.id === sourceTemplateId);
								const isFlatRate = (selectedTemplate?.name || '').trim().toLowerCase() === FLAT_RATE_TEMPLATE_NAME;
								return (
									<React.Fragment key={index}>
										<div className={clsx(styles.itemsRow, isExpanded && styles.itemsRowExpanded)}>
										<div className={clsx(styles.itemsCell, styles.cellItem)}>
											{itemIcon && <Icon imgType={itemIcon.imgType} color={itemIcon.color} width={1.4} />}
											<span className={styles.itemNameText}>{itemName || tab.name || ''}</span>
										</div>

										<div className={clsx(styles.itemsCell, styles.cellQuantity)}>
											{isPayAsYouGo ? (
												singleUsageColumn ? (
													// The usage product is fixed to the one matching the item — read-only, no picker.
													<div className={styles.itemOption}>
														<Icon imgType='meteredHash' color='neutral400' width={1.6} />
														<span className={styles.itemOptionLabel}>{payAsYouGoUsageProductName}</span>
													</div>
												) : (
													<span className={styles.emptyCell}>—</span>
												)
											) : quantityColumn ? (
												<input
													type='number'
													min={0}
													className={styles.rowQuantityInput}
													disabled={!isEditMode}
													value={(getTabQuantityValue(tab) as number | string) ?? ''}
													onChange={(event) => onChangeRowQuantity({ index, value: event.target.value, flushToServer: false })}
													onBlur={(event) => onChangeRowQuantity({ index, value: event.target.value, flushToServer: true })}
												/>
											) : (
												<span className={styles.emptyCell}>—</span>
											)}
										</div>

										<div className={clsx(styles.itemsCell, styles.cellPrice)}>
											<span className={styles.priceSummary}>{formatAmountWithCycle(getTabUnitPrice(tab), tab)}</span>
										</div>

										<div className={clsx(styles.itemsCell, styles.cellTotal)}>
											<span className={styles.priceSummary}>{formatAmountWithCycle(getTabAmount(tab), tab)}</span>
										</div>

										<div className={clsx(styles.itemsCell, styles.cellActions)}>
											<button
												type='button'
												className={styles.rowEditButton}
												aria-label={isExpanded ? 'Collapse line item' : 'Expand line item'}
												onClick={() => setEditingIndex(isExpanded ? null : index)}
											>
												<span className={clsx(styles.lineItemChevron, !isExpanded && styles.lineItemChevronCollapsed)}>
													<Icon imgType='arrow_down' color='neutral400' width={1.4} />
												</span>
											</button>
											{isEditMode && (
												<button
													type='button'
													className={styles.rowDeleteButton}
													aria-label={t('removePrice')}
													onClick={() => onTabClose(index)}
												>
													<Icon imgType='delete' color='neutral400' width={1.7} />
												</button>
											)}
										</div>
									</div>

									{isExpanded && (
										<div className={styles.itemsExpandedPanel}>
											<ContractContentV2
												contractType={contractType}
												contract={contract}
												selectedCustomer={selectedCustomer}
												pricingModelTab={tab}
												isEditable={isEditMode}
												isFlatRate={isFlatRate}
												className={pricingModelClassName}
												templateOptions={tabTemplateOptions}
												templateDefaultValue={templateDefaultValue}
												onSelectTemplate={(templateId) => onSelectPricingTemplate(index, templateId)}
												onTogglePayAsYouGo={(isPayAsYouGo) => onTogglePayAsYouGo(index, isPayAsYouGo)}
												canPayAsYouGo={hasMatchingUsageProduct || isPayAsYouGo}
												updatePricingModelData={(data, instantUpdate) => updatePricingModelTabByIndex(index, data, instantUpdate)}
											/>
										</div>
									)}
								</React.Fragment>
							);
						})}
					</div>

						{isEditMode &&
							(draftPrice['__new_price__'] ? (
								<div className={styles.addPriceRow}>
									<Select
										autoFocus
										isSearchable
										menuPlacement='bottom'
										width='100%'
										resetSelectOnClose={false}
										placeholder={t('searchItemByNameOrIdPlaceholder')}
										data={itemOptions}
										onCustomSearch={filterItemOptions}
										addButton={createItemButton((created) => {
											closeDraftPrice('__new_price__');
											onAddNewPrice(created);
										})}
										onChange={(value) => {
											const item = itemsList.find((it) => (it.id || it.name) === value);
											closeDraftPrice('__new_price__');
											item && onAddNewPrice(item);
										}}
									/>
								</div>
							) : (
								<div className={styles.addPriceRow}>
									<button type='button' className={styles.addPriceButton} onClick={() => openDraftPrice('__new_price__')}>
										<Icon imgType='add' color='success' width={1.4} />
										{t('addNewItem')}
									</button>
								</div>
							))}
					</div>
				) : pricingModel.tabs?.length ? (
					<NewPricingModelContent
						pricingModelTab={pricingModel.tabs[selectedTab]}
						selectedRowItem={selectedRowItem}
						className={pricingModelClassName}
						updatePricingModelData={updatePricingModelTab}
					/>
				) : (
					<EmptyPricingModel
					contractType={contractType}
					title={'createPricing'}
					description={'addPricingDescription'}
					subTitle={'createFromCommon'}
					startBlank={onStartBlank}
					openPricingModelCatalog={() => setOpenModal(true)}
					addModel={(model) => {
						setOpenModal(true);
						setSelectedCommonModel(model);
					}}
					className={styles.emptyPricingModel}
				/>
			)}

			<CatalogModalV2
				contractType={contractType}
				editMode={tabContentType === PricingModelTabContentOptions.CONTRACT}
				selectedModel={selectedCommonModel}
				openModal={openModal}
				setOpenModal={(isOpen) => {
					setOpenModal(isOpen);
					setSelectedCommonModel(undefined);
				}}
				onUseThisPricing={(model) => {
					setSelectedCommonModel(undefined);
					handleAddModel(model, true);
				}}
				addNewPricingEmptyProduct={
					tabContentType === PricingModelTabContentOptions.CONTRACT
						? (selectedCatalog) => {
								navigate('../pricing-models', {
									replace: true,
									state: {
										product: { id: selectedCatalog?.id, name: selectedCatalog?.name },
									},
								});
								setOpenModal(false);
						  }
						: undefined
				}
			/>
		</div>
	);
};
